import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { Reveal } from "@/components/Reveal";
import { IdentityLabel, MetaTag } from "@/components/ui-bits";

export default function Blog() {
  const { slug: categorySlug } = useParams();
  const { data: posts = [] } = useQuery({ queryKey: ["blog", categorySlug || "all"], queryFn: async () => (await api.get("/blog", { params: categorySlug ? { category: categorySlug } : {} })).data });
  const { data: categories = [] } = useQuery({ queryKey: ["blog-categories"], queryFn: async () => (await api.get("/blog-categories")).data });
  const activeCat = categories.find((c) => c.slug === categorySlug);
  const { pageContent } = useSite();
  const content = pageContent.blog || {};

  return (
    <div data-testid="blog-page">
      <Seo title="Digitalis Insights | Blog Digital Marketing Medan" description="Insight, panduan, dan tips digital marketing untuk bisnis di Medan dari tim Digitalis." canonicalPath={categorySlug ? `/blog/category/${categorySlug}` : "/blog"} />
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pt-10 md:pt-14 pb-10 md:pb-14">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, ...(activeCat ? [{ label: activeCat.name }] : [])]} />
        <div className="mt-7"><IdentityLabel>{content.eyebrow || "Insight Praktis"}</IdentityLabel><h1 className="font-display font-extrabold tracking-tightest leading-tight text-[2.35rem] sm:text-5xl md:text-6xl">{content.title || "Digitalis Insights"}</h1></div>
        <p className="text-muted-foreground text-base md:text-lg max-w-xl mt-4">{content.description || "Panduan singkat untuk membantu bisnis mengambil keputusan digital dengan lebih cepat."}</p>
        {content.show_categories !== false && <div className="flex flex-wrap gap-2 mt-8">
          <Link to="/blog" className={`filter-chip ${!categorySlug ? "filter-chip-active" : ""}`}>Semua</Link>
          {categories.map((c) => <Link key={c.slug} to={`/blog/category/${c.slug}`} className={`filter-chip ${categorySlug === c.slug ? "filter-chip-active" : ""}`} data-testid={`blog-cat-${c.slug}`}>{c.name}</Link>)}
        </div>}
      </section>
      {content.show_posts !== false && <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 md:py-14">
          {posts.length === 0 ? (
            <p className="text-muted-foreground py-20 text-center">Belum ada artikel yang dipublikasikan pada kategori ini.</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-6 lg:grid-cols-3 lg:gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.05}>
                  <Link to={`/blog/${p.slug}`} className="group block" data-testid={`blog-post-${p.slug}`}>
                    <div className="relative mb-3 aspect-[4/3] overflow-hidden rounded-2xl border border-border sm:mb-5 sm:rounded-3xl"><img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" /></div>
                    <MetaTag className="mb-2 max-w-full truncate">{p.category}</MetaTag>
                    <h2 className="break-words font-display text-sm font-bold leading-tight group-hover:text-primary sm:text-xl">{p.title}</h2>
                    <p className="mt-2 hidden text-sm text-muted-foreground line-clamp-2 sm:block">{p.excerpt}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>}
    </div>
  );
}
